import React, { useMemo, useState } from 'react'
import type {
  DocumentData,
  DocumentSectionOverride,
  WorkSpecRow,
} from '../../types/agreement'
import { getDocumentTemplate } from '../../lib/proposalDocumentTemplates'
import {
  FREQUENCY_KIND_OPTIONS,
  composeFrequency,
  isNumericFrequencyKind,
  parseFrequency,
  type WorkSpecFrequencyKind,
} from '../../lib/workSpecFrequency'

const OTHER_GROUP = '__other__'

interface DocumentDataEditorProps {
  serviceType: string
  phase: 'proposal' | 'contract'
  value: DocumentData
  onChange: (next: DocumentData) => void
}

const UNIT_OPTIONS = [
  { value: 'per_hour', label: 'Per hour' },
  { value: 'per_apartment', label: 'Per apartment' },
  { value: 'per_unit', label: 'Per unit' },
  { value: 'flat', label: 'Flat rate' },
]

let idSeq = 0
function newId(prefix: string) {
  idSeq += 1
  return `${prefix}-${Date.now()}-${idSeq}`
}

export const DocumentDataEditor: React.FC<DocumentDataEditorProps> = ({
  serviceType,
  phase,
  value,
  onChange,
}) => {
  const template = getDocumentTemplate(serviceType)
  const dd = value || {}
  const family = template.family

  const patch = (changes: Partial<DocumentData>) => onChange({ ...dd, ...changes })

  // Stable list of group options derived from the service-type template defaults.
  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(template.defaultWorkSpecs.map((w) => w.group).filter((g): g is string => !!g))
      ),
    [template]
  )
  // Rows the user explicitly switched to a custom ("Other…") group.
  const [customGroupRows, setCustomGroupRows] = useState<Set<string>>(new Set())
  const markCustom = (id: string, isCustom: boolean) =>
    setCustomGroupRows((prev) => {
      const next = new Set(prev)
      if (isCustom) next.add(id)
      else next.delete(id)
      return next
    })
  // Rows the user explicitly switched to a custom ("Other…") frequency. Tracked
  // separately because an empty custom string is indistinguishable from "none".
  const [customFreqRows, setCustomFreqRows] = useState<Set<string>>(new Set())
  const markFreqCustom = (id: string, isCustom: boolean) =>
    setCustomFreqRows((prev) => {
      const next = new Set(prev)
      if (isCustom) next.add(id)
      else next.delete(id)
      return next
    })

  // ---- Sections ----
  const sectionOverride = (id: string): DocumentSectionOverride =>
    (dd.sections || []).find((s) => s.id === id) || { id, included: true }

  const updateSection = (id: string, changes: Partial<DocumentSectionOverride>) => {
    const existing = dd.sections || []
    const found = existing.find((s) => s.id === id)
    const next = found
      ? existing.map((s) => (s.id === id ? { ...s, ...changes } : s))
      : [...existing, { id, included: true, ...changes }]
    patch({ sections: next })
  }

  // ---- Work specs ----
  const workSpecs: WorkSpecRow[] = dd.workSpecs || []
  const updateWorkSpec = (id: string, changes: Partial<WorkSpecRow>) =>
    patch({ workSpecs: workSpecs.map((w) => (w.id === id ? { ...w, ...changes } : w)) })
  const addWorkSpec = () =>
    patch({
      workSpecs: [
        ...workSpecs,
        { id: newId('ws'), group: groupOptions[0] || '', task: '', frequency: '' },
      ],
    })
  const removeWorkSpec = (id: string) =>
    patch({ workSpecs: workSpecs.filter((w) => w.id !== id) })

  const inputCls =
    'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1'

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Document Settings</h3>
        <p className="text-xs text-gray-500">
          These prefill the generated proposal/contract PDF and can be edited per document.
        </p>
      </div>

      {/* Supplies & equipment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Supplies furnished by</label>
          <select
            value={dd.suppliesBy || 'company'}
            onChange={(e) => patch({ suppliesBy: e.target.value as DocumentData['suppliesBy'] })}
            className={inputCls}
          >
            <option value="company">RBC</option>
            <option value="customer">Customer</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Equipment furnished by</label>
          <select
            value={dd.equipmentBy || 'company'}
            onChange={(e) => patch({ equipmentBy: e.target.value as DocumentData['equipmentBy'] })}
            className={inputCls}
          >
            <option value="company">RBC</option>
            <option value="customer">Customer</option>
          </select>
        </div>
      </div>

      {/* Additional work */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Additional / special-work rate ($)</label>
          <input
            type="number"
            step="0.01"
            value={dd.additionalWork?.rate ?? ''}
            onChange={(e) =>
              patch({
                additionalWork: {
                  rate: parseFloat(e.target.value) || 0,
                  unit: dd.additionalWork?.unit || 'per_hour',
                },
              })
            }
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Billed</label>
          <select
            value={dd.additionalWork?.unit || 'per_hour'}
            onChange={(e) =>
              patch({
                additionalWork: {
                  rate: dd.additionalWork?.rate || 0,
                  unit: e.target.value,
                },
              })
            }
            className={inputCls}
          >
            {UNIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Hourly crew (size @ rate)</label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Size"
              value={dd.crew?.size ?? ''}
              onChange={(e) =>
                patch({ crew: { size: parseInt(e.target.value) || 0, rate: dd.crew?.rate || 0 } })
              }
              className={inputCls}
            />
            <input
              type="number"
              step="0.01"
              placeholder="$/hr"
              value={dd.crew?.rate ?? ''}
              onChange={(e) =>
                patch({ crew: { size: dd.crew?.size || 0, rate: parseFloat(e.target.value) || 0 } })
              }
              className={inputCls}
            />
          </div>
        </div>
      </div>

      {/* Snow pricing */}
      {(family === 'maintenance' || family === 'prevailing_wage') && (
        <div className="border border-gray-200 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={!!dd.snow?.enabled}
              onChange={(e) =>
                patch({
                  snow: {
                    enabled: e.target.checked,
                    thresholdInches: dd.snow?.thresholdInches ?? 8,
                    overRate: dd.snow?.overRate ?? 150,
                    underRate: dd.snow?.underRate ?? 95,
                    includedCount: dd.snow?.includedCount ?? 0,
                  },
                })
              }
            />
            Snow removal pricing
          </label>
          {dd.snow?.enabled && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <div>
                <label className={labelCls}>Threshold (in)</label>
                <input
                  type="number"
                  value={dd.snow.thresholdInches}
                  onChange={(e) =>
                    patch({ snow: { ...dd.snow!, thresholdInches: parseFloat(e.target.value) || 0 } })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>At/above ($)</label>
                <input
                  type="number"
                  value={dd.snow.overRate}
                  onChange={(e) =>
                    patch({ snow: { ...dd.snow!, overRate: parseFloat(e.target.value) || 0 } })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Below ($)</label>
                <input
                  type="number"
                  value={dd.snow.underRate}
                  onChange={(e) =>
                    patch({ snow: { ...dd.snow!, underRate: parseFloat(e.target.value) || 0 } })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Included count</label>
                <input
                  type="number"
                  value={dd.snow.includedCount}
                  onChange={(e) =>
                    patch({ snow: { ...dd.snow!, includedCount: parseInt(e.target.value) || 0 } })
                  }
                  className={inputCls}
                />
                <p className="text-xs text-gray-400 mt-1">Counts only snowfalls below the threshold.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prevailing wage extras */}
      {family === 'prevailing_wage' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-200 rounded-lg p-3">
          <div>
            <label className={labelCls}>Prevailing wage rate valid until</label>
            <input
              type="date"
              value={dd.pw?.rateExpiryDate || ''}
              onChange={(e) =>
                patch({
                  pw: {
                    rateExpiryDate: e.target.value || null,
                    ptoHolidayBilledSeparately: dd.pw?.ptoHolidayBilledSeparately ?? true,
                  },
                })
              }
              className={inputCls}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={dd.pw?.ptoHolidayBilledSeparately ?? true}
                onChange={(e) =>
                  patch({
                    pw: {
                      rateExpiryDate: dd.pw?.rateExpiryDate || null,
                      ptoHolidayBilledSeparately: e.target.checked,
                    },
                  })
                }
              />
              PTO / holiday pay billed separately
            </label>
          </div>
        </div>
      )}

      {/* Term auto-renew (contracts) */}
      {phase === 'contract' && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!dd.autoRenew}
            onChange={(e) => patch({ autoRenew: e.target.checked })}
          />
          Contract auto-renews annually
        </label>
      )}

      {/* Work specifications */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-800">Work Specifications</h4>
          <button
            type="button"
            onClick={addWorkSpec}
            className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
          >
            + Add task
          </button>
        </div>
        <div className="space-y-2">
          {workSpecs.length === 0 && (
            <p className="text-xs text-gray-400">No tasks yet. Defaults will be used if left empty.</p>
          )}
          {workSpecs.map((w) => {
            const isCustom =
              groupOptions.length === 0 ||
              customGroupRows.has(w.id) ||
              (!!w.group && !groupOptions.includes(w.group))
            const parsedFreq = parseFrequency(w.frequency)
            const freqIsCustom = customFreqRows.has(w.id) || parsedFreq.kind === 'other'
            const freq = { ...parsedFreq, kind: freqIsCustom ? ('other' as const) : parsedFreq.kind }
            return (
              <div key={w.id} className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-3 space-y-1">
                  <select
                    value={isCustom ? OTHER_GROUP : w.group}
                    onChange={(e) => {
                      if (e.target.value === OTHER_GROUP) {
                        markCustom(w.id, true)
                        if (groupOptions.includes(w.group)) updateWorkSpec(w.id, { group: '' })
                      } else {
                        markCustom(w.id, false)
                        updateWorkSpec(w.id, { group: e.target.value })
                      }
                    }}
                    className={inputCls}
                  >
                    {groupOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                    <option value={OTHER_GROUP}>Other…</option>
                  </select>
                  {isCustom && (
                    <input
                      placeholder="Custom group"
                      value={w.group}
                      onChange={(e) => updateWorkSpec(w.id, { group: e.target.value })}
                      className={inputCls}
                    />
                  )}
                </div>
                <input
                  placeholder="Task"
                  value={w.task}
                  onChange={(e) => updateWorkSpec(w.id, { task: e.target.value })}
                  className={`${inputCls} col-span-4`}
                />
                <div className="col-span-4 space-y-1">
                  <div className="flex gap-1 items-start">
                    <div className="flex-1 min-w-0">
                      <select
                        value={freq.kind}
                        onChange={(e) => {
                          const kind = e.target.value as WorkSpecFrequencyKind
                          if (kind === 'other') {
                            markFreqCustom(w.id, true)
                            return
                          }
                          markFreqCustom(w.id, false)
                          updateWorkSpec(w.id, {
                            frequency: composeFrequency({ kind, count: freq.count, custom: freq.custom }),
                          })
                        }}
                        className={inputCls}
                        title="Frequency"
                      >
                        {FREQUENCY_KIND_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isNumericFrequencyKind(freq.kind) && (
                      <div className="w-16 shrink-0">
                        <input
                          type="number"
                          min={1}
                          value={freq.count}
                          onChange={(e) =>
                            updateWorkSpec(w.id, {
                              frequency: composeFrequency({
                                kind: freq.kind,
                                count: parseInt(e.target.value, 10) || 1,
                                custom: freq.custom,
                              }),
                            })
                          }
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>
                  {freq.kind === 'other' && (
                    <input
                      placeholder="Custom frequency"
                      value={freq.custom}
                      onChange={(e) => updateWorkSpec(w.id, { frequency: e.target.value })}
                      className={inputCls}
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeWorkSpec(w.id)}
                  className="col-span-1 text-red-500 hover:text-red-700 text-sm mt-1.5"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Section include/exclude + overrides */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Document Sections</h4>
        <div className="space-y-2">
          {template.sections.map((section) => {
            const ov = sectionOverride(section.id)
            return (
              <div key={section.id} className="border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={ov.included !== false}
                      onChange={(e) => updateSection(section.id, { included: e.target.checked })}
                    />
                    {section.heading}
                  </label>
                </div>
                {ov.included !== false && section.kind === 'text' && (
                  <textarea
                    placeholder="Leave blank to use the standard wording"
                    value={ov.textOverride || ''}
                    onChange={(e) => updateSection(section.id, { textOverride: e.target.value })}
                    rows={2}
                    className={`${inputCls} mt-2`}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default DocumentDataEditor
